/**
 * Copyright (c) 2018 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	andreas muelder - itemis AG
 *
 */
import * as joint from "@clientio/rappid";
import { dia, shapes, ui, util } from "@clientio/rappid";
import * as ace from "ace-builds";
import { getConfig } from "../../../config";
import { MessageEventBus } from "../../event-bus";
import { AceFactory, DARK_MODE_THEME, LIGHT_MODE_THEME } from "../language-server/ace-factory";
import AceLanguageServer from "../language-server/ace-lsp-adapter";
import { setSyntaxHighlighting } from "../language-server/ace-settings";
import { actionState } from "../rappid/action-state";
import RappidContext from "../rappid/rappid-context";
import { PreferenceStore } from "../services/preference-store";
import { activeView, updateVisibleStates } from "../subdiagram-viewer";
import { BaseNode, BaseView } from "./base/base-node";
import { DirectEdit } from "./base/direct-edit";
import DocumentationElement from "./base/documentation-element";
import { ElementController } from "./base/element-controller";
import { NodeLabel } from "./base/labeled-node";
import NamedElement from "./base/named-element";
import SpecificationElement from "./base/specification-element";
import { deselectAll, difference, removeStaticAttributes } from "./base/utils";
import { Region, RegionView } from "./region";
import { SubdiagramHighlighter } from "./tools/subdiagram-button";
import { AceEditorState } from "../rappid/ace-editor-state";
import { NameTextEditor } from "../text-editor/name-text-editor";

const Position = ui.Halo.HandlePosition;

export const DEFAULT_NAME_FONTSIZE = 11;
export const LABEL_PADDING = 20.75;
export const MIN_WIDTH = 60;
export const MIN_HEIGHT = 60;

export class State extends BaseNode implements SpecificationElement, NamedElement, DocumentationElement {
  defaults() {
    return {
      ...super.defaults,
      type: "State",
      fixedRatio: false,
      embedable: true,
      linkable: true,
      size: {
        height: MIN_HEIGHT,
        width: MIN_WIDTH,
      },
      attrs: {
        root: {
          magnetSelector: "body",
        },
        body: {
          refWidth: "100%",
          refHeight: "100%",
          strokeWidth: PreferenceStore.INSTANCE.theme.model.state.borderWidth,
          strokeDasharray: "none",
          rx: "5",
          fillOpacity: 1,
          stroke: null,
          fill: null,
        },
        name: {
          textVerticalAnchor: "middle",
          textAnchor: "middle",
          refX: "50%",
          refY: "15px",
          fontSize: DEFAULT_NAME_FONTSIZE,
          fontWeight: "Bold",
          display: "inline",
          fontFamily: PreferenceStore.INSTANCE.theme.model.text.fontFamily,
          text: "State",
          textWrap: {
            maxLineCount: 1,
            ellipsis: true,
            preserveSpaces: true,
            width: "calc(w - 4)",
          },
          fill: null,
        },
        namebg: {
          ref: "body",
          fill: "transparent",
          refY: "7px",
          refX: "2px",
          stroke: "transparent",
          strokeWidth: 0.4,
          height: "15px",
          cursor: "grab",
          width: "calc(w - 4)",
        },
        subdiagram: {
          active: false,
        },
        layout: {
          region: "horizontal",
        },
        specification: {
          text: "",
          show: true,
        },
        documentation: {
          text: "",
        },
        autosize: {
          width: false,
          height: false,
        },
        embeds: {},
      },
    };
  }

  markup = [
    {
      tagName: "rect",
      selector: "body",
    },
    {
      tagName: "g",
      selector: "namegroup",
      children: [
        { tagName: "rect", selector: "namebg" },
        { tagName: "text", selector: "name" },
      ],
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialize(...args: any[]) {
    super.initialize(...args);
    const size = this.size();
    if (size.width < 0 || size.height < 0) {
      const autosizeWidth = size.width < 0;
      const autosizeHeight = size.height < 0;

      this.attr(["autosize", "width"], autosizeWidth, { silent: true });
      this.attr(["autosize", "height"], autosizeHeight, { silent: true });

      // Set to minimum valid size temporarily
      this.size(autosizeWidth ? MIN_WIDTH : size.width, autosizeHeight ? MIN_HEIGHT : size.height, { silent: true });
    }
  }

  getName(): string {
    return this.attr(["name", "text"]);
  }

  setName(name: string) {
    this.attr(["name", "text"], name);
  }

  getSpecification(): string {
    return this.attr(["specification", "text"]);
  }

  setSpecification(spec: string) {
    this.attr(["specification", "text"], spec);
  }

  getSpecificationType(): string {
    return "state";
  }

  getDocumentation(): string {
    return this.attr(["documentation", "text"]);
  }

  setDocumentation(documentation: string) {
    this.attr(["documentation", "text"], documentation);
  }

  getDocumentationType(): string {
    return "State";
  }

  getFillColor(): string {
    return this.attr(["body", "fill"]);
  }

  setFillColor(color: string | null) {
    this.attr(["body", "fill"], color);
    if (color === null) {
      this.removeAttr(["body", "fill"]);
    }
  }

  getBorderColor(): string {
    return this.attr(["body", "stroke"]);
  }

  setBorderColor(color: string | null) {
    this.attr(["body", "stroke"], color);
    if (color === null) {
      this.removeAttr(["body", "stroke"]);
    }
  }
  getNameColor(): string {
    return this.attr(["name", "fill"]);
  }

  setNameColor(color: string | null) {
    this.attr(["name", "fill"], color);
    if (color === null) {
      this.removeAttr(["name", "fill"]);
    }
  }

  getNameFontSize(): number {
    return this.attr(["name", "fontSize"]);
  }

  setNameFontSize(size: number) {
    this.attr(["name", "fontSize"], size);
  }

  getBorderWidth(): number {
    return this.attr(["body", "strokeWidth"]);
  }

  setBorderWidth(width: number) {
    this.attr(["body", "strokeWidth"], width);
  }

  setSubdiagram(value: boolean) {
    this.attr(["subdiagram", "active"], value);
  }

  getSubdiagram() {
    return this.attr(["subdiagram", "active"]);
  }

  setLayoutDirection(direction: string) {
    this.attr(["layout", "region"], direction);
  }

  getLayoutDirection() {
    return this.attr(["layout", "region"]);
  }

  getShowSpecification(): boolean {
    return this.attr(["specification", "show"]);
  }

  setShowSpecification(show: boolean) {
    this.attr(["specification", "show"], show);
  }

  resetDefaults() {
    this.setFillColor(null);
    this.setNameColor(null);
    this.setNameFontSize(DEFAULT_NAME_FONTSIZE);
    this.setBorderColor(null);
    this.setBorderWidth(PreferenceStore.INSTANCE.theme.model.state.borderWidth);
  }

  getEmbeddedRegions(): Region[] {
    const direction = this.getLayoutDirection() || "horizontal";
    const regions = this.getEmbeddedCells()
      .filter((c) => c instanceof Region)
      .map((l) => l as Region);
    if (direction === "horizontal") {
      return regions.sort((a, b) => {
        return a.getBBox().x - b.getBBox().x;
      });
    } else {
      return regions.sort((a, b) => {
        return a.getBBox().y - b.getBBox().y;
      });
    }
  }

  toJSON() {
    const result = super.toJSON();
    result.attrs = difference(result.attrs, this.defaults().attrs);
    result.attrs = {
      ...result.attrs,
      name: {
        text: this.getName(),
        ...(this.getNameColor() && { fill: this.getNameColor() }),
        ...(this.getNameFontSize() && { fontSize: this.getNameFontSize() }),
      },
      ...(this.getSubdiagram() && { active: this.getSubdiagram() }),
    };

    // Restore -1 for autosized dimensions
    const autosizeWidth = this.attr(["autosize", "width"]);
    const autosizeHeight = this.attr(["autosize", "height"]);
    if (autosizeWidth || autosizeHeight) {
      result.size = {
        width: autosizeWidth ? -1 : result.size.width,
        height: autosizeHeight ? -1 : result.size.height,
      };
    }

    // Filter out autosize attr
    if (result.attrs.autosize) {
      delete result.attrs.autosize;
    }

    return removeStaticAttributes(result);
  }

  unembed(cell: dia.Cell | dia.Cell[], opt?: dia.Graph.Options) {
    // do not unembed regions that are moved in subdiagrams
    if (cell instanceof Region && cell.movingInSubdiagram) {
      return;
    }
    return super.unembed(cell, opt);
  }

  embed(
    cell: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions> | dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions>[],
    opt?: dia.Graph.Options
  ): this {
    // Dont re-embed a NodeLabel because they are already embeded See #442
    if (cell instanceof NodeLabel) {
      return this;
    }
    // unembed regions manually, as they aren't unembedded while draggin in subdiagrams
    if (cell instanceof Region && cell.movingInSubdiagram) {
      cell.movingInSubdiagram = false;
      activeView.value.model.unembed(cell);
    }
    return super.embed(cell, opt);
  }

  isVertical() {
    const direction = this.getLayoutDirection() || "horizontal";
    return direction === "vertical";
  }

  changeSize(direction: dia.Direction, view: StateView) {
    const regions = this.getEmbeddedCells().filter((c) => c instanceof Region) as Region[];
    if (regions.length === 0 || this.getSubdiagram()) {
      return;
    }

    switch (direction) {
      case "right": {
        this.afterResizeRight(regions, view);
        break;
      }
      case "bottom": {
        this.afterResizeBottom(regions, view);
        break;
      }
      case "top": {
        this.afterResizeTop(regions, view);
        break;
      }
      case "left": {
        this.afterResizeLeft(regions, view);
        break;
      }
      case "bottom-right": {
        this.changeSize("right", view);
        this.changeSize("bottom", view);
        break;
      }
      case "top-right": {
        this.changeSize("right", view);
        this.changeSize("top", view);
        break;
      }
      case "bottom-left": {
        this.changeSize("left", view);
        this.changeSize("bottom", view);
        break;
      }
      case "top-left": {
        this.changeSize("left", view);
        this.changeSize("top", view);
        break;
      }
    }
  }

  private afterResizeRight(regions: Region[], view: StateView) {
    if (this.isVertical()) {
      regions.forEach((r) => {
        r.size(this.size().width - StateView.Padding * 2, r.size().height);
      });
    } else {
      const regionWidth = regions.reduce((sum, r) => {
        sum += r.size().width;
        return sum;
      }, 0);
      const lastRegion = regions.at(regions.length - 1);
      const regionsWithoutLast = regionWidth - lastRegion.size().width;
      const calculatedWidth = this.size().width - regionsWithoutLast - StateView.Padding * 2;
      lastRegion.size(calculatedWidth, lastRegion.size().height);
    }
  }

  private afterResizeBottom(regions: Region[], view: StateView) {
    if (this.isVertical()) {
      const regionHeight = regions.reduce((sum, r) => {
        sum += r.size().height;
        return sum;
      }, 0);
      const lastRegion = regions.at(regions.length - 1);
      const regionsWithoutLast = regionHeight - lastRegion.size().height;
      const offsetY = this.getYOffset();
      const calculatedHeight = this.size().height - regionsWithoutLast - offsetY;
      lastRegion.size(lastRegion.size().width, calculatedHeight);
    } else {
      const offsetY = this.getYOffset();
      regions.forEach((r) => {
        r.size(r.size().width, this.size().height - offsetY);
      });
    }
  }

  private afterResizeTop(regions: Region[], view: StateView) {
    if (this.isVertical()) {
      const regionHeight = regions.reduce((sum, r) => {
        sum += r.size().height;
        return sum;
      }, 0);
      const firstRegion = regions.at(0);
      const regionsWithoutFirst = regionHeight - firstRegion.size().height;
      const offsetY = this.getYOffset();
      const calculatedHeight = this.size().height - regionsWithoutFirst - offsetY;
      firstRegion.size(firstRegion.size().width, calculatedHeight, { direction: "top" });
    } else {
      const offsetY = this.getYOffset();
      let offsetX = StateView.Padding;
      regions.forEach((r) => {
        r.size(r.size().width, this.size().height - offsetY, { direction: "top" });
        offsetX += r.size().width;
      });
    }
  }

  private afterResizeLeft(regions: Region[], view: StateView) {
    if (this.isVertical()) {
      regions.forEach((r) => {
        r.size(this.size().width - StateView.Padding * 2, r.size().height, { direction: "left" });
      });
    } else {
      const regionWidth = regions.reduce((sum, r) => {
        sum += r.size().width;
        return sum;
      }, 0);
      const firstRegion = regions.at(0);
      const regionsWithoutFirst = regionWidth - firstRegion.size().width;
      const calculatedWidth = this.size().width - regionsWithoutFirst - StateView.Padding * 2;
      firstRegion.size(calculatedWidth, firstRegion.size().height, { direction: "left" });
    }
  }

  getMinSize(direction: dia.Direction): { width: number; height: number } {
    const regions = this.getEmbeddedCells().filter((c) => c instanceof Region) as Region[];
    if (regions.length === 0 || this.getSubdiagram()) {
      return { width: MIN_WIDTH, height: MIN_HEIGHT };
    }
    const state = this as State;
    switch (direction) {
      case "right": {
        return this.getMinSizeRight(state, regions);
      }
      case "left": {
        return this.getMinSizeLeft(state, regions);
      }
      case "bottom": {
        return this.getMinSizeBottom(state, regions);
      }
      case "top": {
        return this.getMinSizeTop(state, regions);
      }

      case "bottom-right": {
        const width = this.getMinSize("right").width;
        const height = this.getMinSize("bottom").height;
        return { width, height };
      }
      case "top-right": {
        const width = this.getMinSize("right").width;
        const height = this.getMinSize("top").height;
        return { width, height };
      }
      case "bottom-left": {
        const width = this.getMinSize("left").width;
        const height = this.getMinSize("bottom").height;
        return { width, height };
      }
      case "top-left": {
        const width = this.getMinSize("left").width;
        const height = this.getMinSize("top").height;
        return { width, height };
      }
      default: {
        return { width: MIN_WIDTH, height: MIN_HEIGHT };
      }
    }
  }

  private getMinSizeRight(state: State, regions: Region[]) {
    if (state.isVertical()) {
      let calculatedWidth = Math.max(...regions.map((r) => r.getMinSize("right").width));
      calculatedWidth += StateView.Padding * 2;

      return { width: calculatedWidth, height: 0 };
    } else {
      const lastRegion = regions[regions.length - 1];
      const lastRegionMinSize = lastRegion.getMinSize("right");
      const totalWidth = regions.reduce((sum, r) => sum + r.size().width, 0);
      const widthWithoutLast = totalWidth - lastRegion.size().width;
      const calculatedWidth = lastRegionMinSize.width + widthWithoutLast + StateView.Padding * 2;

      return { width: calculatedWidth, height: 0 };
    }
  }

  private getMinSizeLeft(state: State, regions: Region[]): { width: number; height: number } {
    if (state.isVertical()) {
      let calculatedWidth = Math.max(...regions.map((r) => r.getMinSize("left").width));
      calculatedWidth += StateView.Padding * 2;

      return { width: calculatedWidth, height: 0 };
    } else {
      const firstRegion = regions[0];
      const firstRegionMinSize = firstRegion.getMinSize("left");
      const totalWidth = regions.reduce((sum, r) => sum + r.size().width, 0);
      const widthWithoutFirst = totalWidth - firstRegion.size().width;
      const calculatedWidth = firstRegionMinSize.width + widthWithoutFirst + StateView.Padding * 2;

      return { width: calculatedWidth, height: 0 };
    }
  }

  private getMinSizeBottom(state: State, regions: Region[]): { width: number; height: number } {
    if (state.isVertical()) {
      const lastRegion = regions[regions.length - 1];
      const lastRegionMinSize = lastRegion.getMinSize("bottom");
      const totalHeight = regions.reduce((sum, r) => sum + r.size().height, 0);
      const heightWithoutLast = totalHeight - lastRegion.size().height;
      let calculatedHeight = lastRegionMinSize.height + heightWithoutLast;
      calculatedHeight += this.getYOffset();

      return { width: 0, height: calculatedHeight };
    } else {
      let calculatedHeight = Math.max(...regions.map((r) => r.getMinSize("bottom").height));
      calculatedHeight += this.getYOffset();

      return { width: 0, height: calculatedHeight };
    }
  }

  private getMinSizeTop(state: State, regions: Region[]): { width: number; height: number } {
    if (state.isVertical()) {
      const firstRegion = regions[0];
      const firstRegionMinSize = firstRegion.getMinSize("top");
      const totalHeight = regions.reduce((sum, r) => sum + r.size().height, 0);
      const heightWithoutFirst = totalHeight - firstRegion.size().height;
      let calculatedHeight = firstRegionMinSize.height + heightWithoutFirst;
      calculatedHeight += this.getYOffset();

      return { width: 0, height: calculatedHeight };
    } else {
      let calculatedHeight = Math.max(...regions.map((r) => r.getMinSize("top").height));
      calculatedHeight += this.getYOffset();

      return { width: 0, height: calculatedHeight };
    }
  }

  getMaxSize(direction: dia.Direction): { width: number; height: number } {
    const parent = this.getParentCell() as Region;
    if (!parent) {
      return { width: Infinity, height: Infinity };
    }

    switch (direction) {
      case "right": {
        return this.getMaxSizeRight(parent);
      }
      case "bottom": {
        return this.getMaxSizeBottom(parent);
      }
      case "left": {
        return this.getMaxSizeLeft(parent);
      }
      case "top": {
        return this.getMaxSizeTop(parent);
      }
      case "bottom-right": {
        const width = this.getMaxSize("right").width;
        const height = this.getMaxSize("bottom").height;
        return { width, height };
      }
      case "top-right": {
        const width = this.getMaxSize("right").width;
        const height = this.getMaxSize("top").height;
        return { width, height };
      }
      case "bottom-left": {
        const width = this.getMaxSize("left").width;
        const height = this.getMaxSize("bottom").height;
        return { width, height };
      }
      case "top-left": {
        const width = this.getMaxSize("left").width;
        const height = this.getMaxSize("top").height;
        return { width, height };
      }
      default: {
        return { width: Infinity, height: Infinity };
      }
    }
  }

  private getMaxSizeRight(parent: Region): { width: number; height: number } {
    const parentWidth = parent.size().width;
    const calculatedWidth = parentWidth - this.position({ parentRelative: true }).x;
    return { width: calculatedWidth, height: Infinity };
  }

  private getMaxSizeBottom(parent: Region): { width: number; height: number } {
    const parentHeight = parent.size().height;
    const calculatedHeight = parentHeight - this.position({ parentRelative: true }).y;
    return { width: Infinity, height: calculatedHeight };
  }

  private getMaxSizeLeft(parent: Region): { width: number; height: number } {
    const width = this.size().width;
    const calculatedWidth = this.position({ parentRelative: true }).x + width;
    return { width: calculatedWidth, height: Infinity };
  }

  private getMaxSizeTop(parent: Region): { width: number; height: number } {
    const height = this.size().height;
    const calculatedHeight = this.position({ parentRelative: true }).y + height;
    return { width: Infinity, height: calculatedHeight };
  }

  getEditorHeight(preventOverflow = true): number {
    const bbox = this.getBBox();
    const text = this.getShowSpecification() ? this.getSpecification() : this.getDocumentation();
    const length: number = (text.match(/\r?\n/g) || []).length + 1;
    const lineCount = Math.max(1, length);
    let editorHeight = lineCount * StateView.LineHeight;
    if (preventOverflow) {
      editorHeight = Math.min(bbox.height - 34, lineCount * StateView.LineHeight);
    }
    const extra = 4; // accounts for gutter, rounding, scrollbar, etc.
    return editorHeight + extra;
  }

  getYOffset() {
    return this.getEditorHeight() + StateView.MarginTop + StateView.Top + StateView.Padding * 2;
  }

  fitParent(opt?: {
    deep?: boolean;
    padding?: dia.Padding;
    expandOnly?: boolean;
    shrinkOnly?: boolean;
    terminator?: dia.Cell | dia.Cell.ID;
  }): this {
    const newOpt = opt || {};
    newOpt.padding = {
      top: RegionView.PADDING + RegionView.TOP,
      right: RegionView.PADDING,
      bottom: RegionView.PADDING,
      left: RegionView.PADDING,
    };
    return super.fitParent({ ...newOpt });
  }

  fitToChildren(opt?: {
    deep?: boolean;
    padding?: dia.Padding;
    expandOnly?: boolean;
    shrinkOnly?: boolean;
    terminator?: dia.Cell | dia.Cell.ID;
  }): this {
    const newOpt = opt || {};
    newOpt.padding = {
      top: RegionView.PADDING + RegionView.TOP,
      right: RegionView.PADDING,
      bottom: RegionView.PADDING,
      left: RegionView.PADDING,
    };
    // do not change sizes of subdiagram elements
    if (this.getSubdiagram()) {
      return this;
    }
    return super.fitToChildren({ ...newOpt });
  }

  fitRegions(skipFitParent = false) {
    if (this.getSubdiagram()) {
      // Skip the layout part for subdiagrams - transitions are embedded and would
      // trigger unwanted resizing when they shouldn't affect the state's layout.
      // Priorities still need to be kept up to date though.
      this.updateRegionsPriorites();
      return;
    }
    if (activeView.value === null || activeView.value?.model?.id !== this.id) {
      const regions = this.getEmbeddedCells().filter((r) => r instanceof Region);
      if (regions.length === 1) {
        this.createCompositeState(regions[0]);
      } else if (regions.length > 1) {
        if (this.isVertical()) {
          this.updateCompositeStateVertical(regions);
        } else {
          this.updateCompositeStateHorizontal(regions);
        }
      }
      if (!skipFitParent) {
        this.fitParent({ deep: true, expandOnly: true });
        const parent = this.getParentCell() as Region;
        if (parent) {
          const grandParent = parent.getParentCell() as State;
          if (grandParent) {
            grandParent.fitRegions();
          }
        }
      }
    }

    this.updateRegionsPriorites();
  }

  private updateCompositeStateVertical(regions: Region[]) {
    const totalWidth = Math.max(this.size().width, ...regions.map((r) => r.size().width + StateView.Padding * 2));

    const editorHeight = this.getYOffset();
    const totalHeight =
      regions.reduce((sum, r) => {
        return sum + r.size().height;
      }, 0) + editorHeight;

    this.size(totalWidth, totalHeight, { direction: "bottom-right" });

    const x = this.position().x + StateView.Padding;
    let y = this.position().y + editorHeight - StateView.Padding;
    regions.forEach((r) => {
      r.position(x, y, { deep: true });
      r.size(totalWidth - StateView.Padding * 2, r.size().height);
      y += r.size().height;
    });
  }

  private updateCompositeStateHorizontal(regions: Region[]) {
    const totalWidth =
      regions.reduce((sum, r) => {
        return sum + r.size().width;
      }, 0) +
      StateView.Padding * 2;

    const editorHeight = this.getYOffset();
    const totalHeight = Math.max(this.size().height, Math.max(...regions.map((r) => r.size().height + editorHeight)));

    this.size(totalWidth, totalHeight, { direction: "bottom-right" });

    let x = this.position().x + StateView.Padding;
    const y = this.position().y + editorHeight - StateView.Padding;
    regions.forEach((r) => {
      r.position(x, y, { deep: true });
      r.size(r.size().width, totalHeight - editorHeight);
      x += r.size().width;
    });
  }

  private createCompositeState(region: Region) {
    const editorHeight = this.getEditorHeight(false) + StateView.MarginTop + StateView.Top + StateView.Padding * 2;
    const newHeight = Math.max(editorHeight + region.size().height, this.size().height); // take scrollbar into account
    const newWidth = Math.max(region.size().width + StateView.Padding * 2, this.size().width);
    this.size(newWidth, newHeight, { direction: "bottom-right" });
    region.position(StateView.Padding, editorHeight - StateView.Padding, {
      parentRelative: true,
      deep: true,
    });
    region.size(newWidth - StateView.Padding * 2, this.size().height - editorHeight);
  }

  private updateRegionsPriorites() {
    const regions = this.getEmbeddedCells().filter((r) => r instanceof Region);
    regions.forEach((r, index) => {
      r.index(index + 1);
    });
  }
}

export class StateView extends BaseView implements DirectEdit {
  INIT_FLAG = 64;
  MOVE_FLAG = 6;
  RESIZE_FLAG = 28;

  public aceEditor: AceEditorState | undefined;
  private revision = 0;
  selection: joint.ui.Selection | undefined;

  static readonly Padding = 3;
  static readonly MarginTop = 22;
  static readonly Top = 2;
  static readonly Left = 1.75;
  static readonly LineHeight = 14;

  constructor(opt?: dia.CellView.Options<dia.Element>) {
    super(opt);
    this.model.on("change:attrs", (model, _newValue, options) => {
      if (options.propertyPath === "attrs/specification/show") {
        const showSpecification = (model as State).getShowSpecification();
        this.showDocumentation(!showSpecification);
      }
    });
  }

  getState(): State {
    return this.model as State;
  }

  private showDocumentation(value: boolean) {
    const editor = this.aceEditor?.getEditor();
    if (editor) {
      setSyntaxHighlighting(editor, value);
    }
    this.setFocus(false);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmUpdate(flag: number, opt: { [key: string]: any }): number {
    const result = super.confirmUpdate(flag, opt);

    if (!this.aceEditor) {
      this.aceEditor = new AceEditorState();
      this.aceEditor.mount(this);
    } else if (!this.aceEditor.isMounted()) {
      this.aceEditor.unmount();
      this.aceEditor = new AceEditorState();
      this.aceEditor.mount(this);
    }

    if ((flag & this.INIT_FLAG) === this.INIT_FLAG) {
      this.applyAutosizeIfNeeded();
    }
    this.aceEditor?.resize();

    return result;
  }

  private applyAutosizeIfNeeded() {
    const state = this.getState();
    const autosizeWidth = state.attr(["autosize", "width"]);
    const autosizeHeight = state.attr(["autosize", "height"]);

    if (autosizeWidth || autosizeHeight) {
      // Check if DOM is ready
      const nameText = this.vel.findOne("text[joint-selector='name']");
      if (!nameText) {
        // DOM not ready yet, schedule for next tick
        setTimeout(() => this.applyAutosizeIfNeeded(), 0);
        return;
      }

      const bounds = this.calculateFitToContentBounds();
      const currentSize = state.size();

      const size = state.size();
      const newWidth = autosizeWidth ? bounds.width : currentSize.width;
      const newHeight = autosizeHeight ? bounds.height : currentSize.height;

      if (size.width === newWidth && size.height === newHeight) {
        return;
      }

      state.size(newWidth, newHeight);

      this.update();
    }
  }
  pointermove(evt: dia.Event, x: number, y: number) {
    if (this.aceEditor?.isFocused()) return;
    super.pointermove(evt, x, y);
  }

  onRemove() {
    if (this.aceEditor) {
      this.aceEditor.unmount();
      this.aceEditor = undefined;
    }
    SubdiagramHighlighter.remove(this, "subdiagram-indicator");
    super.onRemove();
  }

  updateSubdiagramTool() {
    const state = this.model as State;
    if (state.getSubdiagram() && state.attr(["root", "display"]) === "") {
      SubdiagramHighlighter.add(this, "body", "subdiagram-indicator");
    } else {
      SubdiagramHighlighter.remove(this, "subdiagram-indicator");
    }
  }

  setFocus(focus: boolean) {
    if (!this.aceEditor) {
      return;
    }
    this.aceEditor.setFocus(this, focus);
  }

  calculateFitToContentBounds(direction: "bottom-right" | "top-left" = "bottom-right"): {
    width: number;
    height: number;
  } {
    const state = this.getState();

    const textWrap = this.model.attr("name/textWrap");
    this.model.attr("name/textWrap", null, { async: false, silent: true });

    const nameText = this.vel.findOne("text[joint-selector='name']");
    const nameWidth = nameText.getBBox().width + 5;
    const specWidth = this.getEditorWidth() + StateView.Padding * 2;
    const editorHeight = state.getEditorHeight(false) + StateView.Top + StateView.MarginTop + StateView.Padding * 2;

    const widthDir = direction === "bottom-right" ? "right" : "left";
    const heightDir = direction === "bottom-right" ? "bottom" : "top";

    const width = Math.max(state.getMinSize(widthDir).width, nameWidth, specWidth);
    const height = Math.max(state.getMinSize(heightDir).height, editorHeight);

    this.model.attr("name/textWrap", textWrap, { async: false, silent: true });

    return { width, height };
  }

  fitToContent() {
    const state = this.getState();

    if (!state.getSubdiagram()) {
      const regions = state.getEmbeddedRegions();

      // call embedded cells first, if they are not part of a subdiagram
      regions.forEach((r) => {
        (this.paper.findViewByModel(r) as RegionView)?.fitToContent();
      });
    }

    const boundsBottomRight = this.calculateFitToContentBounds("bottom-right");
    state.size(boundsBottomRight.width, boundsBottomRight.height, { direction: "bottom-right" });
    state.changeSize("bottom-right", this);

    const boundsTopLeft = this.calculateFitToContentBounds("top-left");
    state.size(boundsTopLeft.width, boundsTopLeft.height, { direction: "top-left" });
    state.changeSize("top-left", this);

    this.update();
  }

  private getEditorWidth() {
    const state = this.getState();
    const specification = state.getShowSpecification() ? state.getSpecification() : state.getDocumentation();
    const lines = specification.split(/\r?\n/);
    const longestLine = lines.reduce((max, line) => Math.max(max, line.length), 0);

    const editor = this.aceEditor?.getEditor();
    if (!editor) return 0;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const padding = (editor.renderer as any).$padding ?? 2;
    const extra = 10; // accounts for gutter, rounding, scrollbar, etc.
    const calculatedWidth = longestLine * editor.renderer.characterWidth + padding * 2 + extra;
    return calculatedWidth;
  }

  setTheme(theme: string, opt?: { override?: boolean }): this {
    const editor = this.aceEditor?.getEditor();
    editor?.setTheme(theme === "dark" ? DARK_MODE_THEME : LIGHT_MODE_THEME);
    document.documentElement.style.setProperty(
      "--state-active-bg",
      PreferenceStore.INSTANCE.theme.simulation.state.fillColor
    );
    return super.setTheme(theme, opt);
  }

  editorUndoRedoHandler(model: State) {
    if (!this.aceEditor) {
      return;
    }
    this.aceEditor.editorUndoRedoHandler(model);
  }

  resizeStateForEditor(model: State) {
    const regions = model.getEmbeddedRegions();
    if (regions.length === 0 || model.getSubdiagram()) {
      return;
    }

    const currentHeight = this.model.size().height;
    const state = this.getState();
    const editorHeight = state.getEditorHeight() + StateView.Top + StateView.MarginTop + StateView.Padding * 2;

    const regionsHeight = model.isVertical()
      ? regions.reduce((sum, r) => sum + r.size().height, 0)
      : regions.at(0).size().height;

    const newHeight = editorHeight + regionsHeight;
    model.size(model.size().width, newHeight, { direction: "bottom-right" });

    const diff = newHeight - currentHeight;
    regions.forEach((r) => {
      r.position(r.position().x, r.position().y + diff, { deep: true });
    });

    state.fitRegions();
  }

  updateRegionOrientation() {
    const state = this.getState();
    const regions = state.getEmbeddedCells({ deep: false }).filter((c) => c instanceof Region) as Region[];
    if (regions.length < 2) {
      // region orientation must only be updated if state does contain multiple regions
      return;
    }

    let x = regions.at(0).position({ parentRelative: true }).x;
    let y = regions.at(0).position({ parentRelative: true }).y;
    if (state.isVertical()) {
      const width = Math.max(...regions.map((r) => r.getMinSize("right").width));

      regions.forEach((r) => {
        const height = r.getMinSize("bottom").height;
        r.size(width, height);
        r.position(x, y, { parentRelative: true, deep: true });
        y += height;
      });
    } else {
      const height = Math.max(...regions.map((r) => r.getMinSize("bottom").height));

      regions.forEach((r) => {
        const width = r.getMinSize("right").width;
        r.size(r.getMinSize("right").width, height);
        r.position(x, y, { parentRelative: true, deep: true });
        x += width;
      });
    }

    state.fitRegions();
  }
}
export class StateController extends ElementController {
  constructor(view: dia.CellView, context: RappidContext) {
    super(view, context);
    const stateView = view as StateView;
    stateView.selection = context.selection;
    stateView.on("subdiagram:into", () => {
      updateVisibleStates(context, stateView);
      deselectAll(this.context);
    });
    stateView.on("subdiagram:tool", () => {
      stateView.updateSubdiagramTool();
    });

    stateView.model.on("change:embeds", this.onChangeEmbeds.bind(this));
    stateView.on("change:specification:text", (editor: ace.Ace.Editor, delta: ace.Ace.Delta) => {
      if ((stateView.model as State).getShowSpecification()) {
        stateView.model.attr(["specification", "text"], editor.getSession().getValue());
      } else {
        stateView.model.attr(["documentation", "text"], editor.getSession().getValue());
      }
    });
    this.addAttrChangeListeners();
  }

  private addAttrChangeListeners() {
    const stateView = this.view as StateView;
    this.view.model.on("change:attrs", (model: State, _, options) => {
      if (options.propertyPath === "attrs/layout/region") {
        stateView.updateRegionOrientation();
      }
      if (options.propertyPath === "attrs/specification/text") {
        stateView.editorUndoRedoHandler(model);
      }
      if (options.propertyPath === "attrs/documentation/text") {
        stateView.resizeStateForEditor(model);
      }
      if (options.propertyPath === "attrs/specification/text") {
        stateView.resizeStateForEditor(model);
      }
      stateView.updateSubdiagramTool();
    });
  }

  onChangeEmbeds() {
    if (!actionState.isUndo) {
      const state = (this.view as StateView).getState();
      state.fitRegions();
    }

    const stateView = this.view as StateView;
    // Highlighter automatically handles resize via change:position/size events
    if (stateView.aceEditor) {
      stateView.aceEditor.resize();
    }
  }

  onChangeSize() {
    (this.view as BaseView).displayDiagnostics();
  }

  onCellPointerDown(evt?: dia.Event) {
    if (evt.target.getAttribute("data-action") === "fork") {
      // do not select forked states which breaks undo/redo functionality (#71)
      return;
    }
    super.onCellPointerDown(evt);

    if (evt.target.getAttribute("class") !== "ace_content") {
      (this.view as StateView).setFocus(false);
    }
  }

  onCellPointerUp(evt?: dia.Event) {
    super.onCellPointerUp(evt);
    if (evt.target.getAttribute("class") === "ace_content") {
      return;
    }
  }

  onCellPointerDblClick(evt?: dia.Event) {
    const selector = evt?.target?.getAttribute("joint-selector");
    if (
      selector === "name" ||
      selector === "namebg" ||
      evt.target.getAttribute("class") === "v-line" ||
      selector === null
    ) {
      this.context.nameTextEditor.create(this.view, "name");
    }
  }

  createFreetransform() {
    const view = this.view as StateView;
    const model = view.getState();
    const ft = new (class extends ui.FreeTransform {
      constructor() {
        super({
          theme: "modern",
          cellView: view,
          allowRotation: false,
          resizeDirections: ["top-left", "top-right", "bottom-left", "bottom-right"],
          preserveAspectRatio: view.model.attributes["fixedRatio"],
          minWidth(cell, FreeTransform, direction) {
            const { width } = model.getMinSize(direction);
            return width;
          },
          minHeight(cell, FreeTransform, direction) {
            const { height } = model.getMinSize(direction);
            return height;
          },
          maxWidth(cell, FreeTransform, direction) {
            const { width } = model.getMaxSize(direction);
            return width;
          },
          maxHeight(cell, FreeTransform, direction) {
            const { height } = model.getMaxSize(direction);
            return height;
          },
          padding: 0,
        });

        this.addResizeListeners();
      }

      addResizeListeners() {
        const state = view.getState();
        this.listenTo(
          view.model.graph,
          "batch:stop",
          (data: { freeTransform: string; cell: dia.Cell; relativeDirection: dia.Direction; batchName: string }) => {
            const { freeTransform: cid, cell, relativeDirection, batchName } = data;
            if (cid !== this.cid) return;
            if (batchName !== "resize") return;
            if (state !== cell) return;

            // Disable autosize based on resize direction
            if (relativeDirection.includes("left") || relativeDirection.includes("right")) {
              state.attr(["autosize", "width"], false);
            }
            if (relativeDirection.includes("top") || relativeDirection.includes("bottom")) {
              state.attr(["autosize", "height"], false);
            }

            state.changeSize(relativeDirection, view);
            view.update();
          }
        );
      }
    })();
    ft.render();
  }

  createHalo() {
    const halo = super.createHalo({
      cellView: this.view,
      clone: () => new State(),
    });
    halo.on("action:fork:pointerup", (evt, x, y) => {
      const data = halo.eventData(evt);
      const cloneView = data.cloneView as dia.CellView;
      if (cloneView) {
        if (activeView.value !== null && cloneView.model.getParentCell() === null) {
          cloneView.model.remove();
        }
      }
    });
    halo.on("action:extract:pointerup", (evt) => {
      evt.stopPropagation();
      this.context.commandManager.initBatchCommand();
      this.extractSubdiagram();
      this.context.commandManager.storeBatchCommand();
    });
    halo.on("action:inline:pointerup", (evt) => {
      evt.stopPropagation();
      this.context.commandManager.initBatchCommand();
      this.inlineSubdiagram();
      this.context.commandManager.storeBatchCommand();
    });
    halo.on("action:preferred:pointerdown", (evt) => {
      evt.stopPropagation();
      this.context.commandManager.initBatchCommand();
      this.fitToContent();
      this.context.commandManager.storeBatchCommand();
    });
    return halo;
  }

  private fitToContent() {
    const view = this.view as StateView;
    const state = view.getState();

    // Enable autosize when using fit-to-content
    state.attr(["autosize", "width"], true);
    state.attr(["autosize", "height"], true);

    view.fitToContent();
  }

  private inlineSubdiagram() {
    this.updateSubdiagram();
    const state = (this.view as StateView).getState();
    // Re-order embed list by priority index before layout so that any priority
    // changes made while in the subdiagram view are preserved when inlining.
    const regions = state.getEmbeddedCells().filter((r) => r instanceof Region) as Region[];
    if (regions.length > 1 && regions.every((r) => r.index() > 0)) {
      const sorted = [...regions].sort((a, b) => a.index() - b.index());
      if (sorted.some((r, i) => r !== regions[i])) {
        state.unembed(regions);
        state.embed(sorted, { deep: true });
        return; // fitRegions() is triggered by the change:embeds handler
      }
    }
    state.fitRegions();
  }

  private extractSubdiagram() {
    const state = (this.view as StateView).getState();

    const embeddedCells = state.getEmbeddedCells({ deep: true });

    const canExtract = this.hasIncomingOrOutgoingTransitions(embeddedCells);
    if (!canExtract) {
      MessageEventBus.emit("error", {
        text: "Unable to extract subdiagram. Remove incoming/outcoming transitions.",
        id: "statechart-editor",
      });
      return;
    }

    this.updateSubdiagram();
  }

  private updateSubdiagram() {
    const state = (this.view as StateView).getState();
    state.setSubdiagram(!state.getSubdiagram());
    updateVisibleStates(this.context, activeView.value as StateView);
    deselectAll(this.context);
    this.createHalo();
    this.createFreetransform();
  }

  private hasIncomingOrOutgoingTransitions(embeddedCells: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions>[]) {
    return (
      embeddedCells.filter((cell) => {
        if (cell instanceof BaseNode) {
          const incomingLinks = this.context.graph.getConnectedLinks(cell, {
            inbound: true,
            outbound: true,
          });
          const outsideLinks = incomingLinks.filter((link) => {
            const target = link.getTargetCell();
            const source = link.getSourceCell();
            if (embeddedCells.find((c) => c.id === target.id) && embeddedCells.find((c) => c.id === source.id)) {
              return false;
            }
            return true;
          });
          return outsideLinks.length > 0;
        }
      }).length === 0
    );
  }

  getHaloConfig() {
    const config = super.getHaloConfig();
    const state = (this.view as StateView).getState();
    config.push(
      {
        name: "preferred",
        position: Position.SW,
        events: { pointerdown: "preferred" },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Resize to fit contents",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 15,
          },
        },
      },
      {
        name: "fork",
        position: Position.NE,
        events: {
          pointerdown: "startForking",
          pointermove: "doFork",
          pointerup: "stopForking",
        },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Add outgoing state",
            "data-tooltip-position": "left",
            "data-tooltip-padding": 25,
          },
        },
      }
    );

    const { subdiagramsEnabled } = getConfig();

    if (!subdiagramsEnabled) {
      return config;
    }

    let action: "extract" | "inline" = "extract";
    const inline = state.getSubdiagram();

    if (inline) {
      action = "inline";
    } else {
      action = "extract";
    }

    config.push({
      name: action,
      position: Position.SE,
      events: {
        pointerdown: action,
      },
      attrs: {
        ".handle": {
          "data-tooltip-class-name": "small",
          "data-tooltip": `${action.charAt(0).toUpperCase()}${action.slice(1)} subdiagram`,
          "data-tooltip-position": "right",
          "data-tooltip-padding": 25,
        },
      },
    });

    return config;
  }
}

util.assign(shapes, {
  State,
  StateView,
  StateController,
});
